'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';

export default function TrainerDashboard() {
  const router = useRouter();
  const [user, setUser] = useState<any>(null);
  const [trainerProfile, setTrainerProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Edit states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [editedProfile, setEditedProfile] = useState({
    profilePhoto: '',
    firstName: '',
    lastName: '',
    phone: '',
    location: '',
    bio: '',
    yearsOfExperience: '',
    specializations: [] as string[],
    servicesOffered: [] as string[],
    sessionPricing: '',
    availability: {} as any,
    preferredEnvironment: [] as string[],
    travelDistance: '',
    additionalNotes: '',
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      router.push('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchTrainerProfile(parsedUser.email);
  }, [router]);

  const fetchTrainerProfile = async (email: string) => {
    try {
      const response = await fetch(`/api/trainer/profile?email=${email}`);
      const data = await response.json();

      if (response.ok && data.trainer) {
        setTrainerProfile(data.trainer);
        setEditedProfile({
          profilePhoto: data.trainer.profilePhoto,
          firstName: data.trainer.firstName,
          lastName: data.trainer.lastName,
          phone: data.trainer.phone,
          location: data.trainer.location,
          bio: data.trainer.bio,
          yearsOfExperience: data.trainer.yearsOfExperience.toString(),
          specializations: data.trainer.specializations,
          servicesOffered: data.trainer.servicesOffered,
          sessionPricing: data.trainer.sessionPricing || '',
          availability: data.trainer.availability,
          preferredEnvironment: data.trainer.preferredEnvironment,
          travelDistance: data.trainer.travelDistance || '',
          additionalNotes: data.trainer.additionalNotes || '',
        });
      }
    } catch (error) {
      console.error('Error fetching trainer profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setToast({ message: 'Please upload PNG, JPG, JPEG, or WEBP image', type: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: 'Image must be less than 5MB', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const MAX_SIZE = 1200;
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
        setEditedProfile({ ...editedProfile, profilePhoto: compressedBase64 });
        setToast({ message: 'Profile picture updated! Click Save to apply changes.', type: 'success' });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    router.push('/login');
  };

  const handleSaveProfile = async () => {
    try {
      const response = await fetch('/api/trainer/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          ...editedProfile,
          yearsOfExperience: parseInt(editedProfile.yearsOfExperience),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({ message: 'Profile updated successfully!', type: 'success' });
        setTrainerProfile(data.trainer);
        setEditedProfile({
          profilePhoto: data.trainer.profilePhoto,
          firstName: data.trainer.firstName,
          lastName: data.trainer.lastName,
          phone: data.trainer.phone,
          location: data.trainer.location,
          bio: data.trainer.bio,
          yearsOfExperience: data.trainer.yearsOfExperience.toString(),
          specializations: data.trainer.specializations,
          servicesOffered: data.trainer.servicesOffered,
          sessionPricing: data.trainer.sessionPricing || '',
          availability: data.trainer.availability,
          preferredEnvironment: data.trainer.preferredEnvironment,
          travelDistance: data.trainer.travelDistance || '',
          additionalNotes: data.trainer.additionalNotes || '',
        });
        setIsEditingProfile(false);
      } else {
        setToast({ message: data.error || 'Failed to update profile', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update profile', type: 'error' });
    }
  };

  const handleSaveAvailability = async () => {
    try {
      const response = await fetch('/api/trainer/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          availability: editedProfile.availability,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({ message: 'Availability updated successfully!', type: 'success' });
        setTrainerProfile(data.trainer);
        setIsEditingAvailability(false);
      } else {
        setToast({ message: data.error || 'Failed to update availability', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update availability', type: 'error' });
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setToast({ message: 'Please fill in all password fields', type: 'error' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setToast({ message: 'New password must be at least 8 characters', type: 'error' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setToast({ message: 'New passwords do not match', type: 'error' });
      return;
    }

    try {
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({ message: 'Password changed successfully!', type: 'success' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setIsEditingPassword(false);
      } else {
        setToast({ message: data.error || 'Failed to change password', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to change password', type: 'error' });
    }
  };

  const toggleDayAvailability = (day: string) => {
    setEditedProfile(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          isAvailable: !prev.availability[day]?.isAvailable,
          timeSlots: prev.availability[day]?.timeSlots || []
        }
      }
    }));
  };

  const toggleTimeSlot = (day: string, slot: string) => {
    const dayAvailability = editedProfile.availability[day] || { isAvailable: false, timeSlots: [] };
    const hasSlot = dayAvailability.timeSlots.includes(slot);
    
    setEditedProfile(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...dayAvailability,
          timeSlots: hasSlot
            ? dayAvailability.timeSlots.filter((s: string) => s !== slot)
            : [...dayAvailability.timeSlots, slot]
        }
      }
    }));
  };

  const specializationOptions = [
    'Yoga', 'Pilates', 'Strength Training', 'Cardio', 'HIIT',
    'CrossFit', 'Dance Fitness', 'Prenatal/Postnatal', 'Nutrition',
    'Weight Loss', 'Bodybuilding', 'Flexibility', 'Meditation'
  ];

  const serviceOptions = [
    'One-on-One Training', 'Group Classes', 'Online Coaching',
    'Nutrition Planning', 'Fitness Assessment', 'Custom Workout Plans'
  ];

  const environmentOptions = [
    'Women-Only Gyms', 'Home Visits', 'Online Sessions', 'Outdoor Training'
  ];

  const timeSlots = ['Morning', 'Afternoon', 'Evening'];
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6EFED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8a7296] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!trainerProfile) {
    return (
      <div className="min-h-screen bg-[#F6EFED]">
        <InnerPageHeader />
        <div className="max-w-[1200px] mx-auto px-[20px] py-20">
          <div className="bg-white rounded-[24px] p-12 text-center">
            <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-4">
              No Trainer Profile Found
            </h2>
            <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] mb-8">
              You haven't registered as a trainer yet.
            </p>
            <button
              onClick={() => router.push('/signup/trainer')}
              className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
            >
              Register as Trainer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <InnerPageHeader />

      <section className="py-12">
        <div className="max-w-[1380px] mx-auto px-[20px]">
          <div className="grid md:grid-cols-[280px_1fr] gap-8">
            
            {/* Sidebar */}
            <aside className="bg-white rounded-[24px] p-6 h-fit shadow-sm">
              {/* Profile Picture */}
              <div className="text-center mb-6">
                <div className="w-24 h-24 mx-auto mb-4 relative group cursor-pointer">
                  <img
                    src={editedProfile.profilePhoto || trainerProfile.profilePhoto}
                    alt={`${trainerProfile.firstName} ${trainerProfile.lastName}`}
                    className="w-full h-full rounded-full object-cover border-4 border-[#8a7296]"
                  />
                  {isEditingProfile && (
                    <>
                      <label htmlFor="profile-picture-upload" className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </label>
                      <input
                        id="profile-picture-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleProfilePictureChange}
                        className="hidden"
                      />
                    </>
                  )}
                </div>
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                  {trainerProfile.firstName} {trainerProfile.lastName}
                </h3>
                <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                  {user?.email}
                </p>
                
                {/* Status Badge */}
                <div className="mt-3">
                  <span className={`inline-block px-3 py-1 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium ${
                    trainerProfile.status === 'approved' 
                      ? 'bg-green-100 text-green-700' 
                      : trainerProfile.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {trainerProfile.status === 'approved' && '✓ Approved'}
                    {trainerProfile.status === 'pending' && '⏳ Pending'}
                    {trainerProfile.status === 'rejected' && '✗ Rejected'}
                  </span>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'profile'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  My Profile
                </button>
                <button
                  onClick={() => setActiveTab('availability')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'availability'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Availability
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'settings'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </nav>
            </aside>

            {/* Main Content */}
            <main>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    Welcome back, {trainerProfile.firstName}! 👋
                  </h1>

                  {/* Stats Cards */}
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Specializations</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {trainerProfile.specializations.length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Services</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {trainerProfile.servicesOffered.length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Experience</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {trainerProfile.yearsOfExperience} yrs
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info */}
                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <h2 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                      Quick Info
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Location</p>
                        <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                          📍 {trainerProfile.location}
                        </p>
                      </div>
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Phone</p>
                        <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                          📱 {trainerProfile.phone}
                        </p>
                      </div>
                      {trainerProfile.sessionPricing && (
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Session Pricing</p>
                          <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                            💰 {trainerProfile.sessionPricing}
                          </p>
                        </div>
                      )}
                      {trainerProfile.travelDistance && (
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Travel Distance</p>
                          <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                            🚗 {trainerProfile.travelDistance}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A]">
                      My Profile
                    </h1>
                    {!isEditingProfile ? (
                      <button
                        onClick={() => setIsEditingProfile(true)}
                        className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                      >
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveProfile}
                          className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingProfile(false);
                            fetchTrainerProfile(user.email);
                          }}
                          className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-[24px] p-8 shadow-sm space-y-6">
                    {/* Basic Info */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.firstName : trainerProfile.firstName}
                          onChange={(e) => setEditedProfile({ ...editedProfile, firstName: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.lastName : trainerProfile.lastName}
                          onChange={(e) => setEditedProfile({ ...editedProfile, lastName: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Email (Cannot be changed)
                        </label>
                        <input
                          type="email"
                          value={user?.email}
                          readOnly
                          className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] bg-gray-100 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={isEditingProfile ? editedProfile.phone : trainerProfile.phone}
                          onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Location
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.location : trainerProfile.location}
                          onChange={(e) => setEditedProfile({ ...editedProfile, location: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Years of Experience
                        </label>
                        <input
                          type="number"
                          value={isEditingProfile ? editedProfile.yearsOfExperience : trainerProfile.yearsOfExperience}
                          onChange={(e) => setEditedProfile({ ...editedProfile, yearsOfExperience: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Bio */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                        Professional Bio
                      </label>
                      <textarea
                        value={isEditingProfile ? editedProfile.bio : trainerProfile.bio}
                        onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                        readOnly={!isEditingProfile}
                        rows={6}
                        className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] resize-none ${
                          isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                        }`}
                      />
                    </div>

                    {/* Specializations */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-3">
                        Specializations
                      </label>
                      {isEditingProfile ? (
                        <div className="flex flex-wrap gap-2">
                          {specializationOptions.map((spec) => (
                            <button
                              key={spec}
                              type="button"
                              onClick={() => {
                                const newSpecs = editedProfile.specializations.includes(spec)
                                  ? editedProfile.specializations.filter(s => s !== spec)
                                  : [...editedProfile.specializations, spec];
                                setEditedProfile({ ...editedProfile, specializations: newSpecs });
                              }}
                              className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                                editedProfile.specializations.includes(spec)
                                  ? 'bg-[#8a7296] text-white'
                                  : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                              }`}
                            >
                              {spec}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {trainerProfile.specializations.map((spec: string) => (
                            <span
                              key={spec}
                              className="px-4 py-2 bg-[#8a7296] text-white rounded-full font-['Archivo',sans-serif] text-[14px] font-medium"
                            >
                              {spec}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Services Offered */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-3">
                        Services Offered
                      </label>
                      {isEditingProfile ? (
                        <div className="flex flex-wrap gap-2">
                          {serviceOptions.map((service) => (
                            <button
                              key={service}
                              type="button"
                              onClick={() => {
                                const newServices = editedProfile.servicesOffered.includes(service)
                                  ? editedProfile.servicesOffered.filter(s => s !== service)
                                  : [...editedProfile.servicesOffered, service];
                                setEditedProfile({ ...editedProfile, servicesOffered: newServices });
                              }}
                              className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                                editedProfile.servicesOffered.includes(service)
                                  ? 'bg-[#8a7296] text-white'
                                  : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                              }`}
                            >
                              {service}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {trainerProfile.servicesOffered.map((service: string) => (
                            <span
                              key={service}
                              className="px-4 py-2 bg-[#f5f2f0] text-[#2E2A3A] rounded-full font-['Archivo',sans-serif] text-[14px] font-medium"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Session Pricing & Travel Distance */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Session Pricing
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.sessionPricing : trainerProfile.sessionPricing || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, sessionPricing: e.target.value })}
                          readOnly={!isEditingProfile}
                          placeholder="e.g. £30/hour"
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Travel Distance
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.travelDistance : trainerProfile.travelDistance || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, travelDistance: e.target.value })}
                          readOnly={!isEditingProfile}
                          placeholder="e.g. Within 10 miles"
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Preferred Environment */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-3">
                        Preferred Training Environment
                      </label>
                      {isEditingProfile ? (
                        <div className="flex flex-wrap gap-2">
                          {environmentOptions.map((env) => (
                            <button
                              key={env}
                              type="button"
                              onClick={() => {
                                const newEnv = editedProfile.preferredEnvironment.includes(env)
                                  ? editedProfile.preferredEnvironment.filter(e => e !== env)
                                  : [...editedProfile.preferredEnvironment, env];
                                setEditedProfile({ ...editedProfile, preferredEnvironment: newEnv });
                              }}
                              className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                                editedProfile.preferredEnvironment.includes(env)
                                  ? 'bg-[#8a7296] text-white'
                                  : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                              }`}
                            >
                              {env}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {trainerProfile.preferredEnvironment.map((env: string) => (
                            <span
                              key={env}
                              className="px-4 py-2 bg-[#f5f2f0] text-[#2E2A3A] rounded-full font-['Archivo',sans-serif] text-[14px] font-medium"
                            >
                              {env}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        value={isEditingProfile ? editedProfile.additionalNotes : trainerProfile.additionalNotes || ''}
                        onChange={(e) => setEditedProfile({ ...editedProfile, additionalNotes: e.target.value })}
                        readOnly={!isEditingProfile}
                        rows={3}
                        placeholder="Any additional information..."
                        className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] resize-none ${
                          isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Availability Tab */}
              {activeTab === 'availability' && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A]">
                      Availability
                    </h1>
                    {!isEditingAvailability ? (
                      <button
                        onClick={() => setIsEditingAvailability(true)}
                        className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                      >
                        Edit Availability
                      </button>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveAvailability}
                          className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingAvailability(false);
                            fetchTrainerProfile(user.email);
                          }}
                          className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <div className="space-y-4">
                      {daysOfWeek.map((day) => {
                        const dayData = editedProfile.availability[day] || { isAvailable: false, timeSlots: [] };
                        return (
                          <div key={day} className="p-6 bg-[#f5f2f0] rounded-[16px]">
                            <div className="flex items-center justify-between mb-4">
                              <p className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                                {day}
                              </p>
                              {isEditingAvailability && (
                                <button
                                  onClick={() => toggleDayAvailability(day)}
                                  className={`px-4 py-2 rounded-lg font-['Archivo',sans-serif] text-[14px] font-medium transition-colors ${
                                    dayData.isAvailable
                                      ? 'bg-[#8a7296] text-white'
                                      : 'bg-white text-[#2E2A3A] border border-[#d7d7d7]'
                                  }`}
                                >
                                  {dayData.isAvailable ? 'Available' : 'Not Available'}
                                </button>
                              )}
                            </div>
                            
                            {dayData.isAvailable && (
                              <div className="flex flex-wrap gap-2">
                                {isEditingAvailability ? (
                                  timeSlots.map((slot) => (
                                    <button
                                      key={slot}
                                      type="button"
                                      onClick={() => toggleTimeSlot(day, slot)}
                                      className={`px-4 py-2 rounded-lg font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                                        dayData.timeSlots.includes(slot)
                                          ? 'bg-[#8a7296] text-white'
                                          : 'bg-white text-[#2E2A3A] border border-[#d7d7d7] hover:border-[#8a7296]'
                                      }`}
                                    >
                                      {slot}
                                    </button>
                                  ))
                                ) : (
                                  dayData.timeSlots.map((slot: string) => (
                                    <span
                                      key={slot}
                                      className="px-4 py-2 bg-white text-[#2E2A3A] rounded-lg font-['Archivo',sans-serif] text-[14px] font-medium"
                                    >
                                      {slot}
                                    </span>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    Settings
                  </h1>

                  {/* Change Password */}
                  <div className="bg-white rounded-[24px] p-8 shadow-sm mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A]">
                        Change Password
                      </h2>
                      {!isEditingPassword && (
                        <button
                          onClick={() => setIsEditingPassword(true)}
                          className="px-4 py-2 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-[#7a647d] transition-colors"
                        >
                          Change Password
                        </button>
                      )}
                    </div>

                    {isEditingPassword && (
                      <div className="space-y-4">
                        <div>
                          <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          />
                        </div>
                        <div>
                          <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          />
                        </div>
                        <div>
                          <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          />
                        </div>
                        <div className="flex gap-4 pt-2">
                          <button
                            onClick={handlePasswordChange}
                            className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                          >
                            Update Password
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingPassword(false);
                              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            }}
                            className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
